import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaCheck } from 'react-icons/fa';
import { createEmployeePenalty } from '../../../../redux/Slices/EmployeePenaltiesSlice';
import { getEmployees } from '../../../../redux/Slices/EmployeesSlice';
import { fetchPenaltyTypes } from '../../../../redux/Slices/PenaltyTypesSlice';

const AddEmployeePenalty = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const dir = isRTL ? 'rtl' : 'ltr';
    const textAlign = isRTL ? 'right' : 'left';

    // State
    const [formData, setFormData] = useState({
        employee_id: '',
        penalty_type_id: '',
        amount: '',
        penalty_date: new Date().toISOString().split('T')[0],
        description: '',
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Dropdowns State - Employee
    const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
    const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
    const [employeePage, setEmployeePage] = useState(1);
    const { employees, employeesPagination } = useSelector((state) => state.auth);
    const employeeSearchInputRef = useRef(null);
    const employeeSearchTimeoutRef = useRef(null);
    const employeeTriggerRef = useRef(null);
    const [employeeDropdownRect, setEmployeeDropdownRect] = useState(null);

    // Dropdowns State - Penalty Type
    const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
    const [typeSearchTerm, setTypeSearchTerm] = useState('');
    const [typePage, setTypePage] = useState(1);
    const { penaltyTypes, pagination: typePagination } = useSelector((state) => state.penaltyTypes);
    const typeCurrentPage = typePagination?.current_page || typePagination?.currentPage || 1;
    const typeLastPage = typePagination?.last_page || typePagination?.lastPage || 1;
    const typeSearchInputRef = useRef(null);
    const typeSearchTimeoutRef = useRef(null);
    const typeTriggerRef = useRef(null);
    const [typeDropdownRect, setTypeDropdownRect] = useState(null);

    // Filter Penalty Types locally for search if needed, but we use server search
    // The user mentioned "Penalty Type" last item cut off, let's ensure it has spacer

    // Outside click listener
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown-wrapper')) {
                setEmployeeDropdownOpen(false);
                setTypeDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load Employees when dropdown opens or pagination/search changes
    useEffect(() => {
        if (employeeDropdownOpen) {
            dispatch(getEmployees({
                page: employeePage,
                per_page: 10,
                search: employeeSearchTerm || undefined
            }));
        }
    }, [employeeDropdownOpen, employeePage, employeeSearchTerm, dispatch]);

    // Load Penalty Types when dropdown opens or pagination/search changes
    useEffect(() => {
        if (typeDropdownOpen) {
            dispatch(fetchPenaltyTypes({
                page: typePage,
                per_page: 10,
                search: typeSearchTerm || undefined
            }));
        }
    }, [typeDropdownOpen, typePage, typeSearchTerm, dispatch]);

    // Handle Search Debounce
    useEffect(() => {
        if (employeeSearchTimeoutRef.current) clearTimeout(employeeSearchTimeoutRef.current);
        employeeSearchTimeoutRef.current = setTimeout(() => {
            if (employeeDropdownOpen) setEmployeePage(1);
        }, 500);
        return () => clearTimeout(employeeSearchTimeoutRef.current);
    }, [employeeSearchTerm, employeeDropdownOpen]);

    useEffect(() => {
        if (typeSearchTimeoutRef.current) clearTimeout(typeSearchTimeoutRef.current);
        typeSearchTimeoutRef.current = setTimeout(() => {
            if (typeDropdownOpen) setTypePage(1);
        }, 500);
        return () => clearTimeout(typeSearchTimeoutRef.current);
    }, [typeSearchTerm, typeDropdownOpen]);

    const validate = () => {
        const newErrors = {};
        if (!formData.employee_id) newErrors.employee_id = t('sidenav.hr.employeePenaltiesNamespace.employeeRequired');
        if (!formData.penalty_type_id) newErrors.penalty_type_id = t('sidenav.hr.employeePenaltiesNamespace.penaltyTypeRequired');
        if (!formData.amount) newErrors.amount = t('sidenav.hr.employeePenaltiesNamespace.amountRequired');
        if (!formData.penalty_date) newErrors.penalty_date = t('sidenav.hr.employeePenaltiesNamespace.dateRequired');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true);
        try {
            const result = await dispatch(createEmployeePenalty(formData)).unwrap();
            if (result && result.isOffline) {
                localStorage.setItem('offline_add_performed', 'true');
                toast.success(isRTL ? 'تم حفظ الجزاء محلياً وسيتم المزامنة عند عودة الاتصال' : 'Penalty saved locally and will be synced when online');
            } else {
                toast.success(t('sidenav.hr.employeePenaltiesNamespace.createSuccess'));
            }
            navigate('/employee-penalties');
        } catch (error) {
            toast.error(error?.message || t('sidenav.hr.employeePenaltiesNamespace.errorSaving'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const updateDropdownRect = (ref, setter) => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setter({ top: rect.bottom + 4, left: rect.left, width: rect.width });
        }
    };

    useEffect(() => {
        if (employeeDropdownOpen) updateDropdownRect(employeeTriggerRef, setEmployeeDropdownRect);
    }, [employeeDropdownOpen]);

    useEffect(() => {
        if (typeDropdownOpen) updateDropdownRect(typeTriggerRef, setTypeDropdownRect);
    }, [typeDropdownOpen]);

    useEffect(() => {
        const handleResizeScroll = () => {
            if (employeeDropdownOpen) updateDropdownRect(employeeTriggerRef, setEmployeeDropdownRect);
            if (typeDropdownOpen) updateDropdownRect(typeTriggerRef, setTypeDropdownRect);
        };
        window.addEventListener('resize', handleResizeScroll, true);
        window.addEventListener('scroll', handleResizeScroll, true);
        return () => {
            window.removeEventListener('resize', handleResizeScroll, true);
            window.removeEventListener('scroll', handleResizeScroll, true);
        };
    }, [employeeDropdownOpen, typeDropdownOpen]);

    const getSelectedEmployeeName = () => {
        if (!formData.employee_id) return t('sidenav.hr.employeePenaltiesNamespace.employee');
        const emp = employees?.find(e => String(e.id) === String(formData.employee_id));
        return emp ? (isRTL ? emp.name_ar : emp.name_en) : formData.employee_id;
    };

    const getSelectedTypeName = () => {
        if (!formData.penalty_type_id) return t('sidenav.hr.employeePenaltiesNamespace.penaltyType');
        const type = penaltyTypes?.find(t => String(t.id) === String(formData.penalty_type_id));
        return type ? (isRTL ? type.name_ar : type.name_en) : formData.penalty_type_id;
    };

    return (
        <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir, textAlign, overflow: 'visible' }}>
            <div style={{ marginBottom: '30px' }}>
                <button onClick={() => navigate('/employee-penalties')} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                    <FaArrowLeft style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} /> {t('btns.back')}
                </button>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>{t('sidenav.hr.employeePenaltiesNamespace.add')}</h1>
            </div>

            <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', padding: '30px', border: '1px solid #333', position: 'relative', overflow: 'visible' }}>
                <form onSubmit={handleSubmit} style={{ overflow: 'visible' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>

                        {/* Employee Dropdown */}
                        <div className="dropdown-wrapper" style={{ position: 'relative', zIndex: employeeDropdownOpen ? 1000 : 1 }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>{t('sidenav.hr.employeePenaltiesNamespace.employee')} <span style={{ color: '#f44336' }}>*</span></label>
                            <div onClick={() => { setEmployeeDropdownOpen(!employeeDropdownOpen); setTypeDropdownOpen(false); }} style={{ padding: '12px', backgroundColor: '#1a1f2e', border: errors.employee_id ? '1px solid #f44336' : '1px solid #333', borderRadius: '8px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{getSelectedEmployeeName()}</span>
                                <span style={{ fontSize: '12px', color: '#666' }}>▼</span>
                            </div>
                            {employeeDropdownOpen && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', zIndex: 1000, marginTop: '5px', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 16px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
                                    <div style={{ padding: '10px', backgroundColor: '#1a1f2e' }}>
                                        <input ref={employeeSearchInputRef} type="text" placeholder={t('sidenav.hr.employeePenaltiesNamespace.searchPlaceholder')} value={employeeSearchTerm} onChange={(e) => setEmployeeSearchTerm(e.target.value)} style={{ width: '100%', padding: '10px', backgroundColor: '#14263b', border: '1px solid #333', borderRadius: '4px', color: 'white', outline: 'none' }} />
                                    </div>

                                    {/* Advanced Pagination for Dropdown - Directly Under Search */}
                                    {employeesPagination && employeesPagination.last_page > 1 && (
                                        <div style={{ padding: '10px', borderTop: '1px solid #333', borderBottom: '1px solid #333', background: '#14263b' }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setEmployeePage(1); }} disabled={(employeesPagination.current_page || 1) === 1} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>««</button>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setEmployeePage(prev => Math.max(1, prev - 1)); }} disabled={(employeesPagination.current_page || 1) === 1} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>‹</button>
                                                {Array.from({ length: Math.min(3, employeesPagination.last_page) }, (_, i) => {
                                                    const pageNum = Math.max(1, Math.min(employeesPagination.last_page - 2, employeesPagination.current_page - 1)) + i;
                                                    if (pageNum <= 0 || pageNum > employeesPagination.last_page) return null;
                                                    return (
                                                        <button key={pageNum} type="button" onClick={(e) => { e.stopPropagation(); setEmployeePage(pageNum); }} style={{ padding: '4px 8px', backgroundColor: pageNum === employeesPagination.current_page ? '#AC2000' : '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>{pageNum}</button>
                                                    );
                                                })}
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setEmployeePage(prev => Math.min(employeesPagination.last_page, prev + 1)); }} disabled={(employeesPagination.current_page || 1) === employeesPagination.last_page} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>›</button>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setEmployeePage(employeesPagination.last_page); }} disabled={(employeesPagination.current_page || 1) === employeesPagination.last_page} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>»»</button>
                                            </div>
                                            <div style={{ color: '#ccc', fontSize: '10px', marginTop: '4px', textAlign: 'center' }}>
                                                {t('common.pagination.page', { defaultValue: 'الصفحة' })} {employeesPagination.current_page || 1} {t('common.pagination.of', { defaultValue: 'من' })} {employeesPagination.last_page || 1}
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ overflowY: 'auto', maxHeight: '350px', padding: '0 10px', paddingBottom: '20px' }}>
                                        {employees?.map(emp => (
                                            <div key={emp.id} onClick={() => { setFormData({ ...formData, employee_id: emp.id }); setEmployeeDropdownOpen(false); }} style={{ padding: '12px', cursor: 'pointer', borderBottom: '1px solid #333', transition: 'background 0.2s', color: 'white' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#2d3343'} onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                                                {isRTL ? emp.name_ar : emp.name_en}
                                            </div>
                                        ))}
                                        {(!employees || employees.length === 0) && (
                                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>{t('sidenav.hr.employeePenaltiesNamespace.noData')}</div>
                                        )}
                                        <div style={{ height: '80px' }}></div>
                                    </div>
                                </div>
                            )}
                            {errors.employee_id && <span style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.employee_id}</span>}
                        </div>

                        {/* Penalty Type Dropdown */}
                        <div className="dropdown-wrapper" style={{ position: 'relative', zIndex: typeDropdownOpen ? 1000 : 1 }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>{t('sidenav.hr.employeePenaltiesNamespace.penaltyType')} <span style={{ color: '#f44336' }}>*</span></label>
                            <div onClick={() => { setTypeDropdownOpen(!typeDropdownOpen); setEmployeeDropdownOpen(false); }} style={{ padding: '12px', backgroundColor: '#1a1f2e', border: errors.penalty_type_id ? '1px solid #f44336' : '1px solid #333', borderRadius: '8px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{getSelectedTypeName()}</span>
                                <span style={{ fontSize: '12px', color: '#666' }}>▼</span>
                            </div>
                            {typeDropdownOpen && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', zIndex: 1000, marginTop: '5px', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 16px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
                                    <div style={{ padding: '10px', backgroundColor: '#1a1f2e' }}>
                                        <input ref={typeSearchInputRef} type="text" placeholder={t('sidenav.hr.employeePenaltiesNamespace.searchPlaceholder')} value={typeSearchTerm} onChange={(e) => setTypeSearchTerm(e.target.value)} style={{ width: '100%', padding: '10px', backgroundColor: '#14263b', border: '1px solid #333', borderRadius: '4px', color: 'white', outline: 'none' }} />
                                    </div>

                                    {/* Advanced Pagination for Dropdown - Directly Under Search */}
                                    {typePagination && typeLastPage > 1 && (
                                        <div style={{ padding: '10px', borderTop: '1px solid #333', borderBottom: '1px solid #333', background: '#14263b' }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setTypePage(1); }} disabled={typeCurrentPage === 1} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>««</button>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setTypePage(prev => Math.max(1, prev - 1)); }} disabled={typeCurrentPage === 1} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>‹</button>
                                                {Array.from({ length: Math.min(3, typeLastPage) }, (_, i) => {
                                                    const pageNum = Math.max(1, Math.min(typeLastPage - 2, typeCurrentPage - 1)) + i;
                                                    if (pageNum <= 0 || pageNum > typeLastPage) return null;
                                                    return (
                                                        <button key={pageNum} type="button" onClick={(e) => { e.stopPropagation(); setTypePage(pageNum); }} style={{ padding: '4px 8px', backgroundColor: pageNum === typeCurrentPage ? '#AC2000' : '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>{pageNum}</button>
                                                    );
                                                })}
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setTypePage(prev => Math.min(typeLastPage, prev + 1)); }} disabled={typeCurrentPage === typeLastPage} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>›</button>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setTypePage(typeLastPage); }} disabled={typeCurrentPage === typeLastPage} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>»»</button>
                                            </div>
                                            <div style={{ color: '#ccc', fontSize: '10px', marginTop: '4px', textAlign: 'center' }}>
                                                {t('common.pagination.page', { defaultValue: 'الصفحة' })} {typeCurrentPage} {t('common.pagination.of', { defaultValue: 'من' })} {typeLastPage}
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ overflowY: 'auto', maxHeight: '350px', padding: '0 10px', paddingBottom: '20px' }}>
                                        {penaltyTypes?.map(type => (
                                            <div key={type.id} onClick={() => { setFormData({ ...formData, penalty_type_id: type.id }); setTypeDropdownOpen(false); }} style={{ padding: '12px', cursor: 'pointer', borderBottom: '1px solid #333', transition: 'background 0.2s', color: 'white' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#2d3343'} onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                                                {isRTL ? type.name_ar : type.name_en}
                                            </div>
                                        ))}
                                        {(!penaltyTypes || penaltyTypes.length === 0) && (
                                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>{t('sidenav.hr.penaltyTypesNamespace.noData')}</div>
                                        )}
                                        <div style={{ height: '80px' }}></div> {/* Spacer to keep last item fully visible */}
                                    </div>
                                </div>
                            )}
                            {errors.penalty_type_id && <span style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.penalty_type_id}</span>}
                        </div>

                        {/* Amount */}
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>{t('sidenav.hr.employeePenaltiesNamespace.amount')} <span style={{ color: '#f44336' }}>*</span></label>
                            <input type="number"  value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: errors.amount ? '1px solid #f44336' : '1px solid #333', borderRadius: '8px', color: 'white', outline: 'none' }} />
                            {errors.amount && <span style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.amount}</span>}
                        </div>

                        {/* Date */}
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>{t('sidenav.hr.employeePenaltiesNamespace.date')} <span style={{ color: '#f44336' }}>*</span></label>
                            <input type="date" value={formData.penalty_date} onChange={(e) => setFormData({ ...formData, penalty_date: e.target.value })} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: errors.penalty_date ? '1px solid #f44336' : '1px solid #333', borderRadius: '8px', color: 'white', outline: 'none' }} />
                            {errors.penalty_date && <span style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.penalty_date}</span>}
                        </div>
                    </div>

                    {/* Description */}
                    <div style={{ marginBottom: '30px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>{t('sidenav.hr.employeePenaltiesNamespace.description')}</label>
                        <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="4" style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', resize: 'vertical', outline: 'none' }} />
                    </div>

                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                        <button
                            type="button"
                            onClick={() => navigate('/employee-penalties')}
                            disabled={isSubmitting}
                            style={{
                                padding: '12px 30px',
                                backgroundColor: '#666',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                fontWeight: 'bold',
                                fontSize: '16px'
                            }}
                        >
                            {t('btns.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                padding: '12px 30px',
                                backgroundColor: '#AC2000',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}
                        >
                            {isSubmitting ? t('btns.saving') : (
                                <>
                                    <FaCheck />
                                    {t('btns.save')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddEmployeePenalty;
